import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, Input, Message, SelectPicker, Tooltip, Whisper, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useCreateActivity,
  useCreateTask,
  useProjectUsers,
  useTaskOrderActualDollarsByTask,
  useTaskOrderTasksSpendingByMonth,
  useProjectActivityForecastSummary,
  useProjects,
  useTaskOrders,
  useTasks,
  useUpdateTask,
} from '../hooks/useQueries';
import { useWbsTemplateItems, useWbsTemplates } from '../hooks/useWbs';
import { formatHours } from '../utils/chartUtils';
import usePermissions from '../hooks/usePermissions';
import { exportSheetsToXlsx } from '../utils/excelExport';
import './TaskOrderDetailPage.css';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const digitsOnly = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

const formatCurrencyInput = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return currencyFormatter.format(n);
};

const parseMoneyFromDisplay = (raw) => {
  const s = String(raw ?? '').replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const formatCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return currencyFormatter.format(n);
};

const formatPercentFromRatio = (ratio) => {
  const n = Number(ratio);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
};

const ALL_TASKS_PICKER_VALUE = '__all_tasks__';

// Actuals pacing thresholds: how many percentage points the % of budget spent may run
// ahead of the % of the schedule elapsed before the budget status section warns.
const PACE_WARN_POINTS = 10;
const PACE_CRITICAL_POINTS = 25;

const TASK_TYPE_OPTIONS = [
  { label: 'Labor', value: 'labor' },
  { label: 'ODC', value: 'odc' },
];

const formatMonthStartLabel = (monthStart) => {
  const s = String(monthStart || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '—';
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

const getCurrentMonthStartIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
};

// How far a month's actuals drifted from its forecast, as a class so both themes get
// their own palette from the tokens rather than a hard-coded hex.
const getDeltaVarianceClass = ({ forecastDollars, deltaDollars }) => {
  const forecast = Math.abs(Number(forecastDollars ?? 0));
  const delta = Math.abs(Number(deltaDollars ?? 0));

  if (forecast <= 0) {
    return delta <= 0 ? 'ppc-tod-var--good' : 'ppc-tod-var--bad';
  }

  const variance = delta / forecast;
  if (variance > 0.10) return 'ppc-tod-var--bad';
  if (variance > 0.05) return 'ppc-tod-var--warn';
  return 'ppc-tod-var--good';
};

// Small progress donut for the summary strip: how far through the schedule we are today.
//
// The middle carries the percentage the ring is already drawing, so dial and number say the
// same thing; the day count reads alongside it as words. The track is drawn from currentColor
// rather than a fixed grey so the ring does not glare on the dark theme.
const ScheduleDonut = ({ percent, daysRemaining, isComplete, size = 46, stroke = 6 }) => {
  const clamped = Math.min(100, Math.max(0, Number(percent) || 0));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (clamped / 100) * circumference;

  const days = Number(daysRemaining);
  const hasDays = Number.isFinite(days);
  const centerText = `${Math.round(clamped)}%`;

  // Only about (size - 2 * stroke) of width clears the ring, so "100%" steps down a size rather
  // than spilling over it.
  const innerWidth = Math.max(1, size - stroke * 2 - 2);
  const fontSize = Math.max(9, Math.min(15, Math.floor(innerWidth / Math.max(1, centerText.length * 0.62))));

  const ariaLabel = isComplete
    ? `Schedule complete, ${clamped.toFixed(0)}% elapsed`
    : `${clamped.toFixed(0)}% of the schedule elapsed, ${hasDays ? days.toLocaleString() : 'unknown'} days remaining`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
      style={{ display: 'block', flex: '0 0 auto' }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.16}
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={clamped >= 100 ? 'var(--tod-danger)' : 'var(--tod-accent)'}
        strokeWidth={stroke}
        strokeLinecap={dash > 0 ? 'round' : 'butt'}
        strokeDasharray={`${dash} ${Math.max(0, circumference - dash)}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        textAnchor="middle"
        dominantBaseline="central"
        style={{ fontSize, fontWeight: 800, fill: 'currentColor', fontVariantNumeric: 'tabular-nums' }}
      >
        {centerText}
      </text>
    </svg>
  );
};

export default function TaskOrderDetailPage() {
  const navigate = useNavigate();
  const params = useParams();
  const projectId = Number(params?.projectId);
  const taskOrderId = Number(params?.taskOrderId);
  const permissions = usePermissions();
  const canManageDisciplines = permissions.canProject(projectId, 'disciplines');
  const canFinance = permissions.canProject(projectId, 'finance');

  const {
    data: projects = [],
    isLoading: projectsLoading,
    error: projectsError,
  } = useProjects();

  const project = useMemo(() => {
    if (!Number.isInteger(projectId)) return null;
    return (projects || []).find((p) => Number(p?.id) === projectId) ?? null;
  }, [projects, projectId]);

  const {
    data: taskOrdersData,
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(projectId, { enabled: Number.isInteger(projectId) });

  const taskOrders = useMemo(() => (Array.isArray(taskOrdersData) ? taskOrdersData : []), [taskOrdersData]);
  const selectedTaskOrder = useMemo(() => {
    if (!Number.isInteger(taskOrderId)) return null;
    return (taskOrders || []).find((o) => Number(o?.id) === taskOrderId) ?? null;
  }, [taskOrders, taskOrderId]);

  const createTask = useCreateTask();
  const createTaskFromWbs = useCreateTask();
  const updateTask = useUpdateTask();
  const createActivityFromWbs = useCreateActivity();

  const { data: tasks = [], isLoading: tasksLoading, error: tasksError } = useTasks(
    { projectId, taskOrderId },
    { enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId) }
  );

  const {
    data: projectUsers = [],
    isLoading: projectUsersLoading,
  } = useProjectUsers(projectId, { enabled: Number.isInteger(projectId) });

  const { data: activityForecastSummary } = useProjectActivityForecastSummary(projectId, null, {
    enabled: Number.isInteger(projectId),
  });

  const { data: actualDollarsSummary } = useTaskOrderActualDollarsByTask(projectId, taskOrderId, {
    enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId),
  });

  const actualDollarsByTaskId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(actualDollarsSummary?.rows) ? actualDollarsSummary.rows : [];
    for (const row of rows) {
      const taskId = Number(row?.taskId);
      if (!Number.isInteger(taskId)) continue;
      const dollars = Number(row?.actualDollars ?? 0);
      map.set(taskId, Number.isFinite(dollars) ? dollars : 0);
    }
    return map;
  }, [actualDollarsSummary?.rows]);

  const forecastHoursByTaskId = useMemo(() => {
    const map = new Map();
    for (const row of activityForecastSummary?.activities ?? []) {
      const taskId = Number(row?.taskId);
      if (!Number.isInteger(taskId)) continue;
      const hours = Number(row?.totalHours ?? 0);
      map.set(taskId, (map.get(taskId) ?? 0) + (Number.isFinite(hours) ? hours : 0));
    }
    return map;
  }, [activityForecastSummary]);

  const tasksWithTotalsRow = useMemo(() => {
    const base = Array.isArray(tasks) ? tasks : [];
    if (base.length === 0) return base;

    let totalForecastHours = 0;
    let totalBudget = 0;
    let totalForecastRevenue = 0;

    for (const t of base) {
      const taskId = Number(t?.id);
      if (Number.isInteger(taskId)) {
        totalForecastHours += Number(forecastHoursByTaskId.get(taskId) ?? 0);
      }

      const budget = Number(t?.budgetTarget ?? t?.budget_target ?? 0);
      if (Number.isFinite(budget)) totalBudget += budget;

      const forecastRevenue = Number(t?.projectedRevenue ?? t?.projected_revenue ?? 0);
      if (Number.isFinite(forecastRevenue)) totalForecastRevenue += forecastRevenue;
    }

    return [
      ...base,
      {
        id: -1,
        __totalsRow: true,
        task: 'Totals',
        totalForecastHours,
        budgetTarget: totalBudget,
        projectedRevenue: totalForecastRevenue,
      },
    ];
  }, [tasks, forecastHoursByTaskId]);

  // Mirrors the discipline page: allocated budget is a dollar figure, so the forecast is compared at its projected revenue.
  const forecastBudgetStatus = useMemo(() => {
    const totalsRow = tasksWithTotalsRow.find((t) => t?.__totalsRow);
    if (!totalsRow) return null;

    const rawOrderBudget = selectedTaskOrder?.budgetTarget ?? selectedTaskOrder?.budget_target;
    const orderBudget =
      rawOrderBudget === null || rawOrderBudget === undefined || rawOrderBudget === ''
        ? null
        : Number(rawOrderBudget);

    const budget = Number.isFinite(orderBudget) ? orderBudget : Number(totalsRow?.budgetTarget ?? 0);
    if (!Number.isFinite(budget) || budget <= 0) return null;

    const forecast = Number(totalsRow?.projectedRevenue ?? 0);
    if (!Number.isFinite(forecast)) return null;

    const delta = forecast - budget;
    return {
      budget,
      forecast,
      delta,
      isOver: delta > 0,
      usedOrderBudget: Number.isFinite(orderBudget),
      percent: (Math.abs(delta) / budget) * 100,
    };
  }, [selectedTaskOrder?.budgetTarget, selectedTaskOrder?.budget_target, tasksWithTotalsRow]);

  const taskPickerData = useMemo(
    () => (Array.isArray(tasks) ? tasks : [])
      .map((t) => ({
        label: String(t?.task || `Task ${t?.id}`),
        value: Number(t?.id),
      }))
      .filter((o) => Number.isInteger(o.value)),
    [tasks]
  );

  const allTaskIdsForSpending = useMemo(
    () => taskPickerData
      .map((o) => Number(o.value))
      .filter((id) => Number.isInteger(id)),
    [taskPickerData]
  );

  const spendingTaskPickerData = useMemo(
    () => [{ label: 'All Disciplines', value: ALL_TASKS_PICKER_VALUE }, ...taskPickerData],
    [taskPickerData]
  );

  const [taskSpendingOpen, setTaskSpendingOpen] = useState(false);
  const [taskSpendingSelection, setTaskSpendingSelection] = useState(ALL_TASKS_PICKER_VALUE);
  const [excludeCurrentMonthActuals, setExcludeCurrentMonthActuals] = useState(false);
  const [excludeCurrentMonthActualsTable, setExcludeCurrentMonthActualsTable] = useState(false);

  useEffect(() => {
    if (!taskSpendingOpen) return;
    if (taskSpendingSelection === ALL_TASKS_PICKER_VALUE) return;

    const selectedId = Number(taskSpendingSelection);
    const stillExists = taskPickerData.some((o) => Number(o.value) === selectedId);
    if (!stillExists) {
      setTaskSpendingSelection(ALL_TASKS_PICKER_VALUE);
    }
  }, [taskSpendingOpen, taskSpendingSelection, taskPickerData]);

  const taskIdsForSpendingQuery = useMemo(() => {
    if (taskSpendingSelection === ALL_TASKS_PICKER_VALUE) return allTaskIdsForSpending;
    const selectedId = Number(taskSpendingSelection);
    return Number.isInteger(selectedId) ? [selectedId] : [];
  }, [allTaskIdsForSpending, taskSpendingSelection]);

  const taskSpendingQueries = useTaskOrderTasksSpendingByMonth(projectId, taskOrderId, taskIdsForSpendingQuery, {
    enabled: taskSpendingOpen && taskIdsForSpendingQuery.length > 0,
  });

  const taskSpendingLoading = useMemo(
    () => Array.isArray(taskSpendingQueries) && taskSpendingQueries.some((q) => q.isLoading || q.isFetching),
    [taskSpendingQueries]
  );

  const taskSpendingError = useMemo(() => {
    if (!Array.isArray(taskSpendingQueries)) return null;
    const firstError = taskSpendingQueries.find((q) => q.error);
    return firstError?.error || null;
  }, [taskSpendingQueries]);

  const taskSpendingRows = useMemo(() => {
    if (!Array.isArray(taskSpendingQueries)) return [];
    const byMonth = new Map();

    for (let i = 0; i < taskSpendingQueries.length; i += 1) {
      const query = taskSpendingQueries[i];
      const rows = Array.isArray(query?.data?.rows) ? query.data.rows : [];

      for (const row of rows) {
        const monthStart = String(row?.monthStart || '').slice(0, 10);
        if (!monthStart) continue;

        const entry = byMonth.get(monthStart) || {
          monthStart,
          forecastDollars: 0,
          actualDollars: 0,
        };

        entry.forecastDollars += Number(row?.forecastDollars ?? 0);
        entry.actualDollars += Number(row?.actualDollars ?? 0);
        byMonth.set(monthStart, entry);
      }
    }

    return Array.from(byMonth.values())
      .map((row) => ({
        ...row,
        deltaDollars: Number(row.actualDollars ?? 0) - Number(row.forecastDollars ?? 0),
      }))
      .sort((a, b) => String(a.monthStart).localeCompare(String(b.monthStart)));
  }, [taskSpendingQueries]);

  const currentMonthStartIso = useMemo(() => getCurrentMonthStartIso(), []);

  const taskTableSpendingQueries = useTaskOrderTasksSpendingByMonth(projectId, taskOrderId, allTaskIdsForSpending, {
    enabled:
      Number.isInteger(projectId)
      && Number.isInteger(taskOrderId)
      && excludeCurrentMonthActualsTable
      && allTaskIdsForSpending.length > 0,
  });

  const currentMonthActualByTaskId = useMemo(() => {
    const out = new Map();
    if (!excludeCurrentMonthActualsTable) return out;
    if (!Array.isArray(taskTableSpendingQueries)) return out;

    for (let i = 0; i < taskTableSpendingQueries.length; i += 1) {
      const query = taskTableSpendingQueries[i];
      const taskId = Number(allTaskIdsForSpending[i]);
      if (!Number.isInteger(taskId)) continue;

      const rows = Array.isArray(query?.data?.rows) ? query.data.rows : [];
      let sumCurrentMonthActual = 0;
      for (const row of rows) {
        const monthStart = String(row?.monthStart || '').slice(0, 10);
        if (monthStart !== currentMonthStartIso) continue;
        const actual = Number(row?.actualDollars ?? 0);
        if (Number.isFinite(actual)) sumCurrentMonthActual += actual;
      }

      out.set(taskId, sumCurrentMonthActual);
    }

    return out;
  }, [allTaskIdsForSpending, currentMonthStartIso, excludeCurrentMonthActualsTable, taskTableSpendingQueries]);

  const displayActualDollarsByTaskId = useMemo(() => {
    if (!excludeCurrentMonthActualsTable) return actualDollarsByTaskId;

    const out = new Map();
    for (const [taskId, totalActual] of actualDollarsByTaskId.entries()) {
      const currentMonthActual = Number(currentMonthActualByTaskId.get(taskId) ?? 0);
      out.set(taskId, Number(totalActual ?? 0) - (Number.isFinite(currentMonthActual) ? currentMonthActual : 0));
    }
    return out;
  }, [actualDollarsByTaskId, currentMonthActualByTaskId, excludeCurrentMonthActualsTable]);

  const totalDisplayActualDollars = useMemo(() => {
    const list = Array.isArray(tasks) ? tasks : [];
    return list.reduce((sum, t) => {
      const taskId = Number(t?.id);
      if (!Number.isInteger(taskId)) return sum;
      const actual = Number(displayActualDollarsByTaskId.get(taskId) ?? 0);
      return sum + (Number.isFinite(actual) ? actual : 0);
    }, 0);
  }, [displayActualDollarsByTaskId, tasks]);

  // Percentage of the task order schedule that has elapsed as of today, used to judge burn pace.
  const timelineProgress = useMemo(() => {
    const pickIso = (...candidates) => {
      for (const c of candidates) {
        const s = String(c ?? '').slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      }
      return null;
    };

    const startIso = pickIso(
      selectedTaskOrder?.startDate, selectedTaskOrder?.start_date,
      project?.startDate, project?.start_date
    );
    const endIso = pickIso(
      selectedTaskOrder?.endDate, selectedTaskOrder?.end_date,
      project?.endDate, project?.end_date
    );
    if (!startIso || !endIso) return null;

    const start = new Date(`${startIso}T00:00:00`).getTime();
    const end = new Date(`${endIso}T00:00:00`).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

    const now = Date.now();
    const ratio = Math.min(1, Math.max(0, (now - start) / (end - start)));
    const daysRemaining = Math.max(0, Math.ceil((end - now) / 86400000));

    return { startIso, endIso, ratio, percent: ratio * 100, daysRemaining, isComplete: now >= end };
  }, [project, selectedTaskOrder]);

  // Warnings driven by actuals, both measured against the allocated budget: budget
  // burning faster than the schedule, and a discipline already past its allocation.
  // The task order's own overrun is the headline row above, not a warning down here.
  // Empty when everything is on pace.
  const actualsBudgetAlerts = useMemo(() => {
    const alerts = [];
    const elapsedPercent = timelineProgress ? timelineProgress.percent : null;

    const pushPaceAlert = ({ key, label, isTotal, actual, budget }) => {
      if (elapsedPercent === null) return;
      if (!Number.isFinite(budget) || budget <= 0) return;
      const usedPercent = (actual / budget) * 100;
      const gap = usedPercent - elapsedPercent;
      if (gap <= PACE_WARN_POINTS) return;
      alerts.push({
        key: `pace:${key}`,
        severity: gap > PACE_CRITICAL_POINTS ? 'critical' : 'warning',
        isTotal,
        label,
        text: `${usedPercent.toFixed(1)}% of budget used (${formatCurrency(actual)} of ${formatCurrency(budget)}) at ${elapsedPercent.toFixed(1)}% of the schedule.`,
      });
    };

    // Returns true when it fired, so the caller can skip the pace alert: once the
    // allocation is blown, "140% of budget used at 30% of the schedule" only repeats it.
    const pushOverBudgetAlert = ({ key, label, isTotal, actual, budget }) => {
      if (!Number.isFinite(budget) || budget <= 0) return false;
      if (actual <= budget) return false;
      alerts.push({
        key: `budget:${key}`,
        severity: 'critical',
        isTotal,
        label,
        text: `Actual ${formatCurrency(actual)} is over the ${formatCurrency(budget)} allocated by ${formatCurrency(actual - budget)}.`,
      });
      return true;
    };

    for (const t of Array.isArray(tasks) ? tasks : []) {
      const taskId = Number(t?.id);
      if (!Number.isInteger(taskId)) continue;

      const actual = Number(displayActualDollarsByTaskId.get(taskId) ?? 0);
      if (!Number.isFinite(actual) || actual <= 0) continue;

      const label = String(t?.task || `Task ${taskId}`);
      const budget = Number(t?.budgetTarget ?? t?.budget_target ?? 0);
      const overBudget = pushOverBudgetAlert({ key: String(taskId), label, isTotal: false, actual, budget });
      if (!overBudget) pushPaceAlert({ key: String(taskId), label, isTotal: false, actual, budget });
    }

    // The task order total only warns about pace here; its overrun, if any, is the
    // "Actuals over budget by" headline row rather than a line in this list.
    const totalActual = Number(totalDisplayActualDollars ?? 0);
    if (forecastBudgetStatus && Number.isFinite(totalActual) && totalActual > 0 && totalActual <= forecastBudgetStatus.budget) {
      pushPaceAlert({
        key: 'total',
        label: 'Task order total',
        isTotal: true,
        actual: totalActual,
        budget: forecastBudgetStatus.budget,
      });
    }

    const severityRank = { critical: 0, warning: 1 };
    return alerts.sort((a, b) => {
      if (a.isTotal !== b.isTotal) return a.isTotal ? -1 : 1;
      const bySeverity = severityRank[a.severity] - severityRank[b.severity];
      if (bySeverity !== 0) return bySeverity;
      return a.label.localeCompare(b.label);
    });
  }, [displayActualDollarsByTaskId, forecastBudgetStatus, tasks, timelineProgress, totalDisplayActualDollars]);

  // Rows arrive sorted by month, so the cumulative actual is a running total down the list —
  // it is what the budget burn is read from, month by month.
  const taskSpendingRowsAdjusted = useMemo(
    () => {
      let cumulativeActualDollars = 0;

      return taskSpendingRows.map((row) => {
        const monthStart = String(row?.monthStart || '').slice(0, 10);
        const isCurrentMonth = monthStart === currentMonthStartIso;
        const rawActualDollars = Number(row?.actualDollars ?? 0);
        const adjustedActualDollars = excludeCurrentMonthActuals && isCurrentMonth ? 0 : rawActualDollars;
        cumulativeActualDollars += adjustedActualDollars;

        return {
          ...row,
          rawActualDollars,
          adjustedActualDollars,
          cumulativeActualDollars,
          deltaDollars: adjustedActualDollars - Number(row?.forecastDollars ?? 0),
          isCurrentMonth,
        };
      });
    },
    [currentMonthStartIso, excludeCurrentMonthActuals, taskSpendingRows]
  );

  const taskSpendingTotalsAdjusted = useMemo(
    () => taskSpendingRowsAdjusted.reduce(
      (acc, row) => {
        acc.forecastDollars += Number(row?.forecastDollars ?? 0);
        acc.actualDollars += Number(row?.adjustedActualDollars ?? 0);
        acc.deltaDollars += Number(row?.deltaDollars ?? 0);
        return acc;
      },
      { forecastDollars: 0, actualDollars: 0, deltaDollars: 0 }
    ),
    [taskSpendingRowsAdjusted]
  );

  const taskSpendingPastTotalsAdjusted = useMemo(
    () => taskSpendingRowsAdjusted.reduce((acc, row) => {
      const monthStart = String(row?.monthStart || '').slice(0, 10);
      const isPastMonth = monthStart && monthStart < currentMonthStartIso;
      if (!isPastMonth) return acc;
      acc.forecastDollars += Number(row?.forecastDollars ?? 0);
      acc.actualDollars += Number(row?.adjustedActualDollars ?? 0);
      acc.deltaDollars += Number(row?.deltaDollars ?? 0);
      return acc;
    }, {
      forecastDollars: 0,
      actualDollars: 0,
      deltaDollars: 0,
    }),
    [currentMonthStartIso, taskSpendingRowsAdjusted]
  );

  const downloadTaskSpendingCsv = () => {
    if (!Array.isArray(taskSpendingRowsAdjusted) || taskSpendingRowsAdjusted.length === 0) return;

    const escapeCsv = (value) => {
      const raw = String(value ?? '');
      if (/[,"\n]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
      return raw;
    };

    // The adjusted rows already carry the exclusion, the delta and the running total, so the
    // file and the table can never disagree.
    const budgetShare = (dollars) => (hasSummaryBudget ? (Number(dollars ?? 0) / summaryBudget).toFixed(4) : '');

    const lines = [
      ['Month', 'Forecast', 'Actual', 'Delta', '% of Budget', 'Cumulative % of Budget'].join(','),
      ...taskSpendingRowsAdjusted.map((row) => {
        const monthStart = String(row?.monthStart || '').slice(0, 10);
        const isPastMonth = monthStart && monthStart < currentMonthStartIso;

        return [
          escapeCsv(formatMonthStartLabel(monthStart)),
          String(Number(row?.forecastDollars ?? 0)),
          String(Number(row?.adjustedActualDollars ?? 0)),
          isPastMonth ? String(Number(row?.deltaDollars ?? 0)) : '',
          budgetShare(row?.adjustedActualDollars),
          budgetShare(row?.cumulativeActualDollars),
        ].join(',');
      }),
      [
        escapeCsv('Totals'),
        String(Number(taskSpendingTotalsAdjusted.forecastDollars ?? 0)),
        String(Number(taskSpendingTotalsAdjusted.actualDollars ?? 0)),
        String(Number(taskSpendingPastTotalsAdjusted.deltaDollars ?? 0)),
        budgetShare(taskSpendingTotalsAdjusted.actualDollars),
        budgetShare(taskSpendingTotalsAdjusted.actualDollars),
      ].join(','),
    ];

    const csv = `${lines.join('\n')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const selectionSlug =
      taskSpendingSelection === ALL_TASKS_PICKER_VALUE
        ? 'all-tasks'
        : `task-${Number(taskSpendingSelection)}`;
    a.download = `task-order-${projectId}-${taskOrderId}-${selectionSlug}-spend-by-month.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadTaskSpendingExcel = async () => {
    if (!Array.isArray(taskSpendingRowsAdjusted) || taskSpendingRowsAdjusted.length === 0) return;

    const selectionSlug =
      taskSpendingSelection === ALL_TASKS_PICKER_VALUE
        ? 'all-tasks'
        : `task-${Number(taskSpendingSelection)}`;

    const sheetRows = taskSpendingRowsAdjusted.map((row) => {
      const monthStart = String(row?.monthStart || '').slice(0, 10);
      const isPastMonth = monthStart && monthStart < currentMonthStartIso;

      return {
        month: formatMonthStartLabel(monthStart),
        forecast: Number(row?.forecastDollars ?? 0),
        actual: Number(row?.adjustedActualDollars ?? 0),
        delta: isPastMonth ? Number(row?.deltaDollars ?? 0) : null,
        budgetShare: hasSummaryBudget ? Number(row?.adjustedActualDollars ?? 0) / summaryBudget : null,
        cumulativeBudgetShare: hasSummaryBudget
          ? Number(row?.cumulativeActualDollars ?? 0) / summaryBudget
          : null,
      };
    });

    sheetRows.push({
      month: 'Totals',
      forecast: Number(taskSpendingTotalsAdjusted.forecastDollars ?? 0),
      actual: Number(taskSpendingTotalsAdjusted.actualDollars ?? 0),
      delta: Number(taskSpendingPastTotalsAdjusted.deltaDollars ?? 0),
      budgetShare: hasSummaryBudget
        ? Number(taskSpendingTotalsAdjusted.actualDollars ?? 0) / summaryBudget
        : null,
      cumulativeBudgetShare: hasSummaryBudget
        ? Number(taskSpendingTotalsAdjusted.actualDollars ?? 0) / summaryBudget
        : null,
    });

    try {
      await exportSheetsToXlsx({
        fileName: `task-order-${projectId}-${taskOrderId}-${selectionSlug}-spend-by-month.xlsx`,
        sheets: [
          {
            name: 'Spend by Month',
            columns: [
              { header: 'Month', key: 'month', width: 18 },
              { header: 'Forecast', key: 'forecast', width: 16, numFmt: '$#,##0;[Red]-$#,##0' },
              { header: 'Actual', key: 'actual', width: 16, numFmt: '$#,##0;[Red]-$#,##0' },
              { header: 'Delta', key: 'delta', width: 16, numFmt: '$#,##0;[Red]-$#,##0' },
              { header: '% of Budget', key: 'budgetShare', width: 14, numFmt: '0.0%' },
              { header: 'Cumulative % of Budget', key: 'cumulativeBudgetShare', width: 22, numFmt: '0.0%' },
            ],
            rows: sheetRows,
          },
        ],
      });
    } catch (err) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(err?.message || err || 'Failed to download Excel.')}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskBudgetTarget, setNewTaskBudgetTarget] = useState('');
  const [newTaskType, setNewTaskType] = useState('labor');
  const [newTaskLeadUserId, setNewTaskLeadUserId] = useState(null);

  const projectMemberOptions = useMemo(
    () => (Array.isArray(projectUsers) ? projectUsers : [])
      .map((u) => ({ label: u?.name || '—', value: Number(u?.id) }))
      .filter((o) => Number.isInteger(o.value)),
    [projectUsers]
  );

  const [addTaskFromWbsOpen, setAddTaskFromWbsOpen] = useState(false);
  const [addTaskFromWbsPending, setAddTaskFromWbsPending] = useState(false);
  const [wbsTemplateId, setWbsTemplateId] = useState(null);
  const [wbsTaskName, setWbsTaskName] = useState('');
  const [wbsSelectedItemIds, setWbsSelectedItemIds] = useState([]);

  const { data: wbsTemplates = [], isLoading: wbsTemplatesLoading, error: wbsTemplatesError } = useWbsTemplates({
    enabled: addTaskFromWbsOpen,
  });

  const { data: wbsItems = [], isLoading: wbsItemsLoading, error: wbsItemsError } = useWbsTemplateItems(wbsTemplateId, {
    enabled: addTaskFromWbsOpen && Number.isInteger(Number(wbsTemplateId)),
  });

  useEffect(() => {
    if (!addTaskFromWbsOpen) return;
    if (Number.isInteger(Number(wbsTemplateId))) return;
    const firstTemplateId = Number(wbsTemplates?.[0]?.id);
    if (!Number.isInteger(firstTemplateId)) return;
    setWbsTemplateId(firstTemplateId);
    if (!String(wbsTaskName || '').trim()) {
      const firstName = String(wbsTemplates?.[0]?.name ?? '').trim();
      if (firstName) setWbsTaskName(firstName);
    }
  }, [addTaskFromWbsOpen, wbsTemplateId, wbsTemplates, wbsTaskName]);

  const sortedWbsItems = useMemo(() => {
    return (Array.isArray(wbsItems) ? wbsItems : [])
      .map((i) => ({
        id: Number(i?.id),
        title: String(i?.title ?? '').trim(),
        assumptions: String(i?.assumptions ?? '').trim(),
        sortOrder: Number(i?.sortOrder ?? 0),
      }))
      .filter((i) => Number.isInteger(i.id) && i.title)
      .sort((a, b) => {
        const so = a.sortOrder - b.sortOrder;
        if (so !== 0) return so;
        return a.id - b.id;
      });
  }, [wbsItems]);

  useEffect(() => {
    if (!addTaskFromWbsOpen) return;
    const ids = sortedWbsItems.map((i) => i.id);
    setWbsSelectedItemIds(ids);
  }, [addTaskFromWbsOpen, wbsTemplateId, sortedWbsItems]);

  // Inline editing state (copied from old Home page behavior)
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState('');
  const [editingBudgetTaskId, setEditingBudgetTaskId] = useState(null);
  const [editingBudgetValue, setEditingBudgetValue] = useState('');
  const [editingBudgetOriginal, setEditingBudgetOriginal] = useState(undefined);
  const editTaskNameInputRef = useRef(null);
  const editBudgetInputRef = useRef(null);
  const skipTaskNameBlurCommitRef = useRef(false);
  const skipBudgetBlurCommitRef = useRef(false);
  const committingTaskNameRef = useRef(false);
  const committingBudgetRef = useRef(false);

  useEffect(() => {
    if (!Number.isInteger(Number(editingTaskId))) return;
    queueMicrotask(() => {
      editTaskNameInputRef.current?.focus?.();
    });
  }, [editingTaskId]);

  useEffect(() => {
    if (!Number.isInteger(Number(editingBudgetTaskId))) return;
    queueMicrotask(() => {
      editBudgetInputRef.current?.focus?.();
    });
  }, [editingBudgetTaskId]);

  const beginEditTaskName = (rowData) => {
    const id = Number(rowData?.id);
    if (!Number.isInteger(id)) return;
    setEditingTaskId(id);
    setEditingTaskName(String(rowData?.task ?? ''));
    setEditingBudgetTaskId(null);
  };

  const cancelEditTaskName = useCallback(() => {
    setEditingTaskId(null);
    setEditingTaskName('');
  }, []);

  const commitEditTaskName = useCallback(async () => {
    if (committingTaskNameRef.current) return;
    const taskId = Number(editingTaskId);
    if (!Number.isInteger(taskId)) return;

    const originalTask = String(editingTaskName || '').trim();
    const nextTask = String(editTaskNameInputRef.current?.value ?? editingTaskName ?? '').trim();
    if (!nextTask) {
      cancelEditTaskName();
      return;
    }

    if (nextTask === originalTask) {
      cancelEditTaskName();
      return;
    }

    committingTaskNameRef.current = true;
    try {
      await updateTask.mutateAsync({ taskId, projectId, payload: { task: nextTask } });
      toaster.push(
        <Message showIcon type="success" closable>
          Discipline name updated.
        </Message>,
        { placement: 'topEnd' }
      );
      cancelEditTaskName();
    } finally {
      committingTaskNameRef.current = false;
    }
  }, [editingTaskId, editingTaskName, updateTask, projectId, cancelEditTaskName]);

  const beginEditBudget = (rowData) => {
    const id = Number(rowData?.id);
    if (!Number.isInteger(id)) return;
    setEditingBudgetTaskId(id);
    const v = rowData?.budgetTarget ?? rowData?.budget_target;
    const original = v === null || v === undefined ? null : Number(v);
    setEditingBudgetOriginal(original);
    setEditingBudgetValue(v === null || v === undefined ? '' : String(v));
    setEditingTaskId(null);
  };

  const cancelEditBudget = useCallback(() => {
    setEditingBudgetTaskId(null);
    setEditingBudgetValue('');
    setEditingBudgetOriginal(undefined);
  }, []);

  const commitEditBudget = useCallback(async () => {
    if (committingBudgetRef.current) return;
    const taskId = Number(editingBudgetTaskId);
    if (!Number.isInteger(taskId)) return;

    const trimmed = String(editBudgetInputRef.current?.value ?? editingBudgetValue ?? '').trim();
    const parsed = trimmed === '' ? null : parseMoneyFromDisplay(trimmed);
    const nextBudgetTarget = parsed;
    if (nextBudgetTarget !== null && !Number.isFinite(nextBudgetTarget)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Budget must be a number.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (Number.isFinite(nextBudgetTarget) && nextBudgetTarget < 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Budget cannot be negative.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const original = editingBudgetOriginal;
    const unchanged =
      (original === null && nextBudgetTarget === null) ||
      (Number.isFinite(original) && Number.isFinite(nextBudgetTarget) && original === nextBudgetTarget);

    if (unchanged) {
      cancelEditBudget();
      return;
    }

    committingBudgetRef.current = true;
    try {
      await updateTask.mutateAsync({ taskId, projectId, payload: { budgetTarget: nextBudgetTarget } });
      toaster.push(
        <Message showIcon type="success" closable>
          Allocated budget updated.
        </Message>,
        { placement: 'topEnd' }
      );
      cancelEditBudget();
    } finally {
      committingBudgetRef.current = false;
    }
  }, [editingBudgetTaskId, editingBudgetValue, editingBudgetOriginal, updateTask, projectId, cancelEditBudget]);

  useEffect(() => {
    const isTaskEditing = Number.isInteger(Number(editingTaskId));
    const isBudgetEditing = Number.isInteger(Number(editingBudgetTaskId));
    if (!isTaskEditing && !isBudgetEditing) return undefined;

    const handlePointerDownCapture = (event) => {
      const target = event?.target;
      if (!(target instanceof Node)) return;

      if (isTaskEditing) {
        const inputEl = editTaskNameInputRef.current;
        if (inputEl && inputEl.contains(target)) return;
        void commitEditTaskName();
        return;
      }

      if (isBudgetEditing) {
        const inputEl = editBudgetInputRef.current;
        if (inputEl && inputEl.contains(target)) return;
        void commitEditBudget();
      }
    };

    document.addEventListener('pointerdown', handlePointerDownCapture, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownCapture, true);
    };
  }, [editingTaskId, editingBudgetTaskId, commitEditTaskName, commitEditBudget]);

  const goToTask = (rowData) => {
    const taskId = Number(rowData?.id);
    if (!Number.isInteger(taskId)) return;
    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
      navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}`);
      return;
    }
    navigate(`/tasks/${taskId}`);
  };

  const submitAddTask = async () => {
    const task = String(newTaskName || '').trim();
    if (!task) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please enter a discipline name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const budgetDigits = digitsOnly(newTaskBudgetTarget);
    const normalizedTaskType = String(newTaskType || '').toLowerCase();
    if (normalizedTaskType !== 'labor' && normalizedTaskType !== 'odc') {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a valid discipline type.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const payload = { task, projectId, taskOrderId, taskType: normalizedTaskType };

    if (!Number.isInteger(newTaskLeadUserId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a discipline lead.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    payload.leadUserId = Number(newTaskLeadUserId);

    if (budgetDigits) {
      const n = Number(budgetDigits);
      if (!Number.isFinite(n)) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Budget must be a number.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
      if (n < 0) {
        toaster.push(
          <Message showIcon type="warning" closable>
            Budget cannot be negative.
          </Message>,
          { placement: 'topEnd' }
        );
        return;
      }
      payload.budgetTarget = n;
    }

    await createTask.mutateAsync(payload);
    setAddTaskOpen(false);
    setNewTaskName('');
    setNewTaskBudgetTarget('');
    setNewTaskType('labor');
    setNewTaskLeadUserId(null);
  };

  const openAddTaskFromWbs = () => {
    setWbsTemplateId(null);
    setWbsTaskName('');
    setWbsSelectedItemIds([]);
    setAddTaskFromWbsOpen(true);
  };

  const submitAddTaskFromWbs = async () => {
    const templateId = Number(wbsTemplateId);
    if (!Number.isInteger(templateId)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select a WBS template.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const task = String(wbsTaskName || '').trim();
    if (!task) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please enter a discipline name.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    if (sortedWbsItems.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Selected template has no items.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    const selectedSet = new Set((Array.isArray(wbsSelectedItemIds) ? wbsSelectedItemIds : []).map((v) => Number(v)));
    const selectedItems = sortedWbsItems.filter((i) => selectedSet.has(i.id));
    if (selectedItems.length === 0) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select at least one activity to import.
        </Message>,
        { placement: 'topEnd' }
      );
      return;
    }

    setAddTaskFromWbsPending(true);
    try {
      const createdTask = await createTaskFromWbs.mutateAsync({ task, projectId, taskOrderId });
      const createdTaskId = Number(createdTask?.id);
      if (!Number.isInteger(createdTaskId)) {
        throw new Error('Failed to create task.');
      }

      for (const item of selectedItems) {
        // eslint-disable-next-line no-await-in-loop
        await createActivityFromWbs.mutateAsync({
          taskId: createdTaskId,
          payload: { name: item.title, assumptions: item.assumptions, leadUserId: null, skipLeadAutoAssign: true },
        });
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Created task and imported {selectedItems.length} activities.
        </Message>,
        { placement: 'topEnd' }
      );

      setAddTaskFromWbsOpen(false);
      setWbsTemplateId(null);
      setWbsTaskName('');
      setWbsSelectedItemIds([]);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          {String(e?.message || e || 'Create from WBS failed.')}
        </Message>,
        { placement: 'topEnd' }
      );
    } finally {
      setAddTaskFromWbsPending(false);
    }
  };

  // ---- Derived values for the summary strip and the table footer ----
  // forecastBudgetStatus already resolves "which budget do we measure against" (the task
  // order's own figure, falling back to the sum of discipline allocations); these reuse it
  // so the tiles, the progress bar and the warnings can never disagree about the number.
  const totalsRow = tasksWithTotalsRow.find((t) => t?.__totalsRow) ?? null;
  const disciplineRows = tasksWithTotalsRow.filter((t) => !t?.__totalsRow);

  const rawSummaryBudget = selectedTaskOrder?.budgetTarget ?? selectedTaskOrder?.budget_target;
  const orderBudgetValue =
    rawSummaryBudget === null || rawSummaryBudget === undefined || rawSummaryBudget === ''
      ? null
      : Number(rawSummaryBudget);

  const summaryBudget = forecastBudgetStatus
    ? forecastBudgetStatus.budget
    : (Number.isFinite(orderBudgetValue) ? orderBudgetValue : Number(totalsRow?.budgetTarget ?? 0));
  const summaryBudgetIsRollup = forecastBudgetStatus ? !forecastBudgetStatus.usedOrderBudget : true;
  const hasSummaryBudget = Number.isFinite(summaryBudget) && summaryBudget > 0;

  const summaryForecast = Number(totalsRow?.projectedRevenue ?? 0);
  const summaryActual = Number(totalDisplayActualDollars ?? 0);

  // Progress bar: forecast consumed out of the allocation. Capped at the full track so an
  // overrun paints the bar solid red rather than overflowing it; the figure above says by
  // how much.
  const forecastUsedPercent = hasSummaryBudget ? (summaryForecast / summaryBudget) * 100 : 0;
  const forecastBarPercent = Math.min(100, Math.max(0, forecastUsedPercent));
  const forecastIsOver = forecastUsedPercent > 100;

  if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) {
    return (
      <div className="ppc-page">
        <Message type="error" showIcon>
          Invalid project/task order.
        </Message>
      </div>
    );
  }

  return (
    <div className="ppc-page ppc-tod">
      <header className="ppc-tod__header">
        <div style={{ minWidth: 0 }}>
          <div className="ppc-tod__crumbs">
            <button
              type="button"
              className="ppc-tod__crumb-link"
              onClick={() => navigate('/')}
            >
              Projects
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <button
              type="button"
              className="ppc-tod__crumb-link"
              onClick={() => navigate(`/projects/${projectId}`)}
            >
              {project?.name || 'Project'}
            </button>
            <i className="bi bi-chevron-right ppc-tod__crumb-sep" aria-hidden="true" />
            <span className="ppc-tod__crumb-current">{selectedTaskOrder?.name || '—'}</span>
          </div>
          <h1 className="ppc-tod__title">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <i className="bi bi-journal-text" aria-hidden="true" />
              Task Order Breakdown
            </span>
          </h1>
        </div>

        <div className="ppc-tod__actions">
          <button
            type="button"
            className="ppc-tod-btn"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            <i className="bi bi-arrow-left" aria-hidden="true" />
            Back to project
          </button>

          <div className="ppc-tod-segment">
            <button
              type="button"
              className="ppc-tod-btn"
              onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/gantt`)}
            >
              Gantt
              <i className="bi bi-bar-chart-steps" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ppc-tod-btn"
              onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals`)}
            >
              Actuals
              <i className="bi bi-clipboard-data" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ppc-tod-btn"
              onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/invoices`)}
            >
              Invoices
              <i className="bi bi-receipt" aria-hidden="true" />
            </button>
            {/* Charts lives on its own route, so the selection travels as query params. */}
            {permissions.can('charts.view') ? (
              <button
                type="button"
                className="ppc-tod-btn"
                onClick={() => navigate(
                  `/charts?projectId=${projectId}&taskOrderId=${taskOrderId}`
                  + `&from=task-order&fromProjectId=${projectId}&fromTaskOrderId=${taskOrderId}`
                )}
              >
                Charts
                <i className="bi bi-bar-chart-line" aria-hidden="true" />
              </button>
            ) : null}
            {canFinance ? (
              <button
                type="button"
                className="ppc-tod-btn"
                onClick={() => navigate(`/projects/${projectId}/task-orders/${taskOrderId}/staffing-outlook`)}
              >
                Staffing Outlook
                <i className="bi bi-people" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {projectsError ? <Message type="error" showIcon>Failed to load projects.</Message> : null}
      {taskOrdersError ? <Message type="error" showIcon>Failed to load task orders.</Message> : null}
      {!projectsLoading && !taskOrdersLoading && !selectedTaskOrder ? (
        <Message type="warning" showIcon>Task order not found for this project.</Message>
      ) : null}

      <section className="ppc-tod-metrics">
        <div className="ppc-tod-metric">
          <div className="ppc-tod-metric__body">
            <div>
              <p className="ppc-tod-metric__label">Total budget</p>
              <p className="ppc-tod-metric__value ppc-tod-num">
                {hasSummaryBudget ? formatCurrency(summaryBudget) : '—'}
              </p>
            </div>
            {/* Silent when the task order carries its own budget; the caveat only
                matters when this figure is really a roll-up of the disciplines. */}
            {summaryBudgetIsRollup ? (
              <div className="ppc-tod-metric__foot">Sum of discipline budgets</div>
            ) : null}
          </div>
        </div>

        <div className="ppc-tod-metric">
          <div className="ppc-tod-metric__body">
            <div>
              <p className="ppc-tod-metric__label">Forecasted amount</p>
              <p className="ppc-tod-metric__value ppc-tod-num">
                {totalsRow ? formatCurrency(summaryForecast) : '—'}
              </p>
            </div>
            {forecastBudgetStatus ? (
              <div
                className={
                  forecastBudgetStatus.delta === 0
                    ? 'ppc-tod-metric__foot'
                    : `ppc-tod-metric__foot ${forecastBudgetStatus.isOver ? 'ppc-tod-metric__foot--bad' : 'ppc-tod-metric__foot--good'}`
                }
              >
                {forecastBudgetStatus.delta === 0 ? (
                  <span>Exactly on budget</span>
                ) : (
                  <>
                    <i
                      className={forecastBudgetStatus.isOver ? 'bi bi-graph-up-arrow' : 'bi bi-graph-down-arrow'}
                      aria-hidden="true"
                    />
                    <span>
                      {`${formatCurrency(Math.abs(forecastBudgetStatus.delta))} ${forecastBudgetStatus.isOver ? 'over' : 'under'} budget`}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <div className="ppc-tod-metric__foot">No allocated budget to compare</div>
            )}
          </div>
        </div>

        <div className="ppc-tod-metric">
          <div className="ppc-tod-metric__body">
            <div>
              <p className="ppc-tod-metric__label">Overall % used</p>
              <p className="ppc-tod-metric__value ppc-tod-num">
                {hasSummaryBudget ? formatPercentFromRatio(summaryActual / summaryBudget) : '—'}
              </p>
            </div>
            <div className="ppc-tod-metric__foot ppc-tod-num">
              {`Actuals: ${formatCurrency(summaryActual)}`}
            </div>
          </div>
        </div>

        <div className="ppc-tod-metric">
          <div className="ppc-tod-metric__body">
            <div>
              <p className="ppc-tod-metric__label">Remaining days</p>
              <p className="ppc-tod-metric__value ppc-tod-num">
                {timelineProgress ? timelineProgress.daysRemaining.toLocaleString() : '—'}
              </p>
            </div>
            <div className="ppc-tod-metric__foot ppc-tod-num">
              {timelineProgress ? `${timelineProgress.startIso} → ${timelineProgress.endIso}` : 'No dates set'}
            </div>
          </div>
          {timelineProgress ? (
            <div className="ppc-tod-metric__donut">
              <ScheduleDonut
                percent={timelineProgress.percent}
                daysRemaining={timelineProgress.daysRemaining}
                isComplete={timelineProgress.isComplete}
                size={58}
                stroke={6}
              />
            </div>
          ) : null}
        </div>
      </section>

      <section className="ppc-tod-card">
        <div className="ppc-tod-card__head">
          <h2 className="ppc-tod-card__title">Disciplines</h2>

          <div className="ppc-tod-card__tools">
            <Checkbox
              checked={excludeCurrentMonthActualsTable}
              onChange={(_value, checked) => setExcludeCurrentMonthActualsTable(Boolean(checked))}
            >
              Exclude current month actuals
            </Checkbox>

            <span className="ppc-tod-card__sep" aria-hidden="true" />

            <div className="ppc-tod-card__tool-group">
              {canFinance ? (
                <button
                  type="button"
                  className="ppc-tod-btn ppc-tod-btn--sm"
                  onClick={() => setTaskSpendingOpen(true)}
                  disabled={tasksLoading || taskPickerData.length === 0}
                >
                  Discipline Spend by Month
                  <i className="bi bi-graph-up" aria-hidden="true" />
                </button>
              ) : null}
              {canManageDisciplines && permissions.can('wbs.view') ? (
                <button
                  type="button"
                  className="ppc-tod-btn ppc-tod-btn--sm"
                  onClick={openAddTaskFromWbs}
                  disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                >
                  Add from WBS
                  <i className="bi bi-diagram-3" aria-hidden="true" />
                </button>
              ) : null}
              {canManageDisciplines ? (
                <button
                  type="button"
                  className="ppc-tod-btn ppc-tod-btn--sm ppc-tod-btn--primary"
                  onClick={() => setAddTaskOpen(true)}
                  disabled={createTask.isPending}
                >
                  Add Discipline
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {tasksError ? (
          <div className="ppc-tod-card__note">
            <Message type="error" showIcon>Failed to load tasks.</Message>
          </div>
        ) : null}

        <div className="ppc-tod-tablewrap">
          <table className="ppc-tod-table">
            <thead>
              <tr>
                <th scope="col">Discipline</th>
                <th scope="col">Activities</th>
                <th scope="col">Total forecasted hours</th>
                <th scope="col">Allocated budget</th>
                <th scope="col">Forecast</th>
                <th scope="col">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Actual $
                    <Whisper
                      trigger="hover"
                      placement="top"
                      speaker={(
                        <Tooltip>
                          Actual $ is not the true invoiced amount, check the invoices tab to see true invoiced amount.
                        </Tooltip>
                      )}
                    >
                      <i className="bi bi-info-circle" aria-hidden="true" style={{ cursor: 'help' }} />
                    </Whisper>
                  </span>
                </th>
                <th scope="col">% Budget Used</th>
                <th scope="col" className="ppc-tod-table__col-center">
                  <span className="visually-hidden">Open</span>
                </th>
              </tr>
            </thead>

            <tbody>
              {tasksLoading && disciplineRows.length === 0 ? (
                <tr>
                  <td className="ppc-tod-table__empty" colSpan={8}>Loading disciplines…</td>
                </tr>
              ) : null}

              {!tasksLoading && disciplineRows.length === 0 ? (
                <tr>
                  <td className="ppc-tod-table__empty" colSpan={8}>
                    No disciplines on this task order yet.
                  </td>
                </tr>
              ) : null}

              {disciplineRows.map((rowData) => {
                const taskId = Number(rowData?.id);
                const isEditingName = Number.isInteger(taskId) && taskId === Number(editingTaskId);
                const isEditingBudget = Number.isInteger(taskId) && taskId === Number(editingBudgetTaskId);

                const activityCount = Array.isArray(rowData?.activities) ? rowData.activities.length : 0;
                const hours = Number.isInteger(taskId) ? (forecastHoursByTaskId.get(taskId) ?? 0) : 0;
                const budgetValue = rowData?.budgetTarget ?? rowData?.budget_target;
                const budget = Number(budgetValue ?? 0);
                const forecast = Number(rowData?.projectedRevenue ?? rowData?.projected_revenue ?? 0);
                const actual = Number.isInteger(taskId) ? Number(displayActualDollarsByTaskId.get(taskId) ?? 0) : 0;
                const exceedsBudget = Number.isFinite(forecast) && Number.isFinite(budget) && budget > 0 && forecast > budget;

                return (
                  <tr
                    key={rowData?.id}
                    className="ppc-tod-table__row"
                    onClick={() => goToTask(rowData)}
                  >
                    <td>
                      {isEditingName ? (
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          style={{ display: 'block' }}
                        >
                          <input
                            ref={editTaskNameInputRef}
                            className="ppc-tod-editinput"
                            type="text"
                            defaultValue={editingTaskName}
                            onBlur={() => {
                              if (skipTaskNameBlurCommitRef.current) {
                                skipTaskNameBlurCommitRef.current = false;
                                return;
                              }
                              void commitEditTaskName();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                void commitEditTaskName();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                e.stopPropagation();
                                skipTaskNameBlurCommitRef.current = true;
                                cancelEditTaskName();
                              }
                            }}
                            disabled={updateTask.isPending}
                          />
                        </span>
                      ) : (
                        <span className="ppc-tod-editable">
                          <span>{rowData?.task}</span>
                          {canManageDisciplines ? (
                            <button
                              type="button"
                              className="ppc-tod-editable__pencil"
                              title="Edit discipline"
                              aria-label="Edit discipline"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                beginEditTaskName(rowData);
                              }}
                            >
                              <i className="bi bi-pencil" aria-hidden="true" />
                            </button>
                          ) : null}
                        </span>
                      )}
                    </td>

                    <td className="ppc-tod-num">{activityCount}</td>

                    <td className="ppc-tod-num ppc-tod-table__muted">{formatHours(hours)}</td>

                    <td className="ppc-tod-num">
                      {isEditingBudget ? (
                        <span
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          style={{ display: 'block' }}
                        >
                          <input
                            ref={editBudgetInputRef}
                            className="ppc-tod-editinput ppc-tod-editinput--num"
                            type="text"
                            defaultValue={editingBudgetValue}
                            onBlur={() => {
                              if (skipBudgetBlurCommitRef.current) {
                                skipBudgetBlurCommitRef.current = false;
                                return;
                              }
                              void commitEditBudget();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                                void commitEditBudget();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                e.stopPropagation();
                                skipBudgetBlurCommitRef.current = true;
                                cancelEditBudget();
                              }
                            }}
                            disabled={updateTask.isPending}
                          />
                        </span>
                      ) : (
                        <span className="ppc-tod-editable">
                          <span>
                            {budgetValue === null || budgetValue === undefined ? '—' : formatCurrency(budgetValue)}
                          </span>
                          {canFinance ? (
                            <button
                              type="button"
                              className="ppc-tod-editable__pencil"
                              title="Edit allocated budget"
                              aria-label="Edit allocated budget"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                beginEditBudget(rowData);
                              }}
                            >
                              <i className="bi bi-pencil" aria-hidden="true" />
                            </button>
                          ) : null}
                        </span>
                      )}
                    </td>

                    <td className="ppc-tod-num">
                      {exceedsBudget ? (
                        <Whisper
                          trigger="hover"
                          placement="top"
                          speaker={<Tooltip>Forecast exceeds allocated budget.</Tooltip>}
                        >
                          <span className="ppc-tod-table__over">
                            {formatCurrency(forecast)}
                            <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                          </span>
                        </Whisper>
                      ) : (
                        <span className="ppc-tod-table__muted">{formatCurrency(forecast)}</span>
                      )}
                    </td>

                    <td className="ppc-tod-num">{formatCurrency(actual)}</td>

                    <td className="ppc-tod-num">
                      {Number.isFinite(budget) && budget > 0 ? formatPercentFromRatio(actual / budget) : '—'}
                    </td>

                    <td className="ppc-tod-table__col-center" style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="ppc-tod-open"
                        title="Open discipline"
                        aria-label={`Open ${rowData?.task ?? 'discipline'}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          goToTask(rowData);
                        }}
                      >
                        <i className="bi bi-chevron-right" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {totalsRow ? (
              <tfoot>
                <tr>
                  <td>Totals</td>
                  <td className="ppc-tod-table__muted">—</td>
                  <td className="ppc-tod-num">{formatHours(totalsRow?.totalForecastHours ?? 0)}</td>
                  <td className="ppc-tod-num">{formatCurrency(totalsRow?.budgetTarget)}</td>
                  <td className="ppc-tod-num">{formatCurrency(summaryForecast)}</td>
                  <td className="ppc-tod-num">{formatCurrency(summaryActual)}</td>
                  <td className="ppc-tod-num">
                    {hasSummaryBudget ? formatPercentFromRatio(summaryActual / summaryBudget) : '—'}
                  </td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>

        {hasSummaryBudget ? (
          <div className="ppc-tod-progress">
            <div className="ppc-tod-progress__row">
              <p className="ppc-tod-progress__label">Forecast vs allocated budget</p>
              <p className={`ppc-tod-progress__stat${forecastIsOver ? ' ppc-tod-progress__stat--over' : ''}`}>
                {forecastIsOver
                  ? `${(forecastUsedPercent - 100).toFixed(2)}% over`
                  : `${(100 - forecastUsedPercent).toFixed(2)}% available`}
              </p>
            </div>
            <div
              className="ppc-tod-progress__track"
              role="progressbar"
              aria-valuenow={Math.round(forecastUsedPercent)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Forecast as a share of the allocated budget"
            >
              <div
                className={`ppc-tod-progress__fill${forecastIsOver ? ' ppc-tod-progress__fill--over' : ''}`}
                style={{ width: `${forecastBarPercent}%` }}
              />
            </div>
            <div className="ppc-tod-progress__scale ppc-tod-num">
              <span className="ppc-tod-progress__detail ppc-tod-num">
                {`Forecast ${formatCurrency(summaryForecast)}`}
              </span>
              <span className="ppc-tod-progress__allocated ppc-tod-num">
                {`${formatCurrency(summaryBudget)} allocated`}
              </span>
            </div>
          </div>
        ) : null}
      </section>

      {actualsBudgetAlerts.length > 0 ? (
        <div className="ppc-tod-alerts ppc-tod-alerts--flat">
          {actualsBudgetAlerts.map((alert) => (
            <div key={alert.key} className={`ppc-tod-alert ppc-tod-alert--${alert.severity}`}>
              <i
                className={alert.severity === 'critical' ? 'bi bi-exclamation-triangle-fill' : 'bi bi-exclamation-circle-fill'}
                aria-hidden="true"
              />
              <span>
                <strong>{alert.label}:</strong>
                {` ${alert.text}`}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <Modal
        className="ppc-tod-modal"
        open={canFinance && taskSpendingOpen}
        onClose={() => setTaskSpendingOpen(false)}
        size="lg"
      >
        <Modal.Header>
          <Modal.Title>Discipline Actual vs Forecast Spend by Month</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ppc-tod-modal__body">
            {taskSpendingError ? (
              <Message type="error" showIcon>
                Failed to load monthly spending.
              </Message>
            ) : null}

            {!taskSpendingError ? (
              <div className="ppc-tod-card">
                <div className="ppc-tod-card__head">
                  <div className="ppc-tod-card__tool-group">
                    <span className="ppc-tod-modal__label">Discipline</span>
                    <SelectPicker
                      size="sm"
                      data={spendingTaskPickerData}
                      value={taskSpendingSelection}
                      onChange={(v) => setTaskSpendingSelection(v ?? ALL_TASKS_PICKER_VALUE)}
                      cleanable={false}
                      searchable
                      style={{ minWidth: 240 }}
                    />
                  </div>

                  <div className="ppc-tod-card__tools">
                    <Checkbox
                      checked={excludeCurrentMonthActuals}
                      onChange={(_value, checked) => setExcludeCurrentMonthActuals(Boolean(checked))}
                    >
                      Exclude current month actuals
                    </Checkbox>

                    <span className="ppc-tod-card__sep" aria-hidden="true" />

                    <div className="ppc-tod-card__tool-group">
                      <button
                        type="button"
                        className="ppc-tod-btn ppc-tod-btn--sm"
                        onClick={() => void downloadTaskSpendingExcel()}
                        disabled={taskSpendingRowsAdjusted.length === 0}
                      >
                        Excel
                        <i className="bi bi-file-earmark-spreadsheet" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="ppc-tod-btn ppc-tod-btn--sm"
                        onClick={downloadTaskSpendingCsv}
                        disabled={taskSpendingRowsAdjusted.length === 0}
                      >
                        CSV
                        <i className="bi bi-filetype-csv" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="ppc-tod-tablewrap">
                  <table className="ppc-tod-table ppc-tod-table--compact">
                    <thead>
                      <tr>
                        <th scope="col">Month</th>
                        <th scope="col">Forecast</th>
                        <th scope="col">Actual</th>
                        <th scope="col">Delta</th>
                        <th scope="col">% budget used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskSpendingLoading ? (
                        <tr>
                          <td className="ppc-tod-table__empty" colSpan={5}>Loading monthly spending…</td>
                        </tr>
                      ) : null}

                      {!taskSpendingLoading && taskSpendingRowsAdjusted.length === 0 ? (
                        <tr>
                          <td className="ppc-tod-table__empty" colSpan={5}>
                            No monthly spending found for this task order.
                          </td>
                        </tr>
                      ) : null}

                      {!taskSpendingLoading
                        ? taskSpendingRowsAdjusted.map((row) => {
                          const monthStart = String(row?.monthStart || '');
                          const forecast = Number(row?.forecastDollars ?? 0);
                          const actual = Number(row?.adjustedActualDollars ?? 0);
                          const rawActual = Number(row?.rawActualDollars ?? 0);
                          const cumulativeActual = Number(row?.cumulativeActualDollars ?? 0);
                          const delta = Number(row?.deltaDollars ?? 0);
                          const isPastMonth = monthStart.slice(0, 10) < currentMonthStartIso;
                          const deltaClass = getDeltaVarianceClass({
                            forecastDollars: forecast,
                            deltaDollars: delta,
                          });
                          const isExcludedCurrentMonth = excludeCurrentMonthActuals && row?.isCurrentMonth;

                          return (
                            <tr key={monthStart}>
                              <td>{formatMonthStartLabel(monthStart)}</td>
                              <td className="ppc-tod-num">{formatCurrency(forecast)}</td>
                              <td className="ppc-tod-num">
                                {isExcludedCurrentMonth ? (
                                  <span className="ppc-tod-excluded" title="Excluded from totals">
                                    {formatCurrency(rawActual)}
                                  </span>
                                ) : (
                                  formatCurrency(actual)
                                )}
                              </td>
                              <td className={`ppc-tod-num ${isPastMonth ? deltaClass : 'ppc-tod-var--none'}`}>
                                {isPastMonth ? formatCurrency(delta) : '—'}
                              </td>
                              <td className="ppc-tod-num" title={`${formatCurrency(cumulativeActual)} spent through this month`}>
                                {hasSummaryBudget ? formatPercentFromRatio(cumulativeActual / summaryBudget) : '—'}
                              </td>
                            </tr>
                          );
                        })
                        : null}
                    </tbody>

                    {!taskSpendingLoading && taskSpendingRowsAdjusted.length > 0 ? (
                      <tfoot>
                        <tr>
                          <td>Totals</td>
                          <td className="ppc-tod-num">
                            {formatCurrency(taskSpendingTotalsAdjusted.forecastDollars ?? 0)}
                          </td>
                          <td className="ppc-tod-num">
                            {formatCurrency(taskSpendingTotalsAdjusted.actualDollars ?? 0)}
                          </td>
                          <td
                            className={`ppc-tod-num ${getDeltaVarianceClass({
                              forecastDollars: taskSpendingPastTotalsAdjusted.forecastDollars,
                              deltaDollars: taskSpendingPastTotalsAdjusted.deltaDollars,
                            })}`}
                          >
                            {formatCurrency(taskSpendingPastTotalsAdjusted.deltaDollars ?? 0)}
                          </td>
                          <td className="ppc-tod-num">
                            {hasSummaryBudget
                              ? formatPercentFromRatio(
                                Number(taskSpendingTotalsAdjusted.actualDollars ?? 0) / summaryBudget
                              )
                              : '—'}
                          </td>
                        </tr>
                      </tfoot>
                    ) : null}
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        open={addTaskOpen}
        onClose={() => {
          setAddTaskOpen(false);
          setNewTaskName('');
          setNewTaskBudgetTarget('');
          setNewTaskType('labor');
          setNewTaskLeadUserId(null);
        }}
        size="sm"
        className="ppc-tod-modal"
      >
        <Modal.Header>
          <Modal.Title>Add Discipline</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ppc-tod-modal__form">
            <div className="ppc-tod-modal__field">
              <div className="ppc-tod-modal__label">Discipline name</div>
              <Input value={newTaskName} onChange={(v) => setNewTaskName(v)} placeholder="Enter discipline name" />
            </div>

            <div className="ppc-tod-modal__field">
              <div className="ppc-tod-modal__label">Budget (optional)</div>
              <Input
                value={newTaskBudgetTarget}
                onChange={(v) => {
                  const digits = digitsOnly(v);
                  if (!digits) {
                    setNewTaskBudgetTarget('');
                    return;
                  }
                  const n = Number(digits);
                  setNewTaskBudgetTarget(Number.isFinite(n) ? formatCurrencyInput(n) : '');
                }}
                placeholder="$25,000"
                style={{ textAlign: 'left' }}
                disabled={!canFinance}
              />
            </div>

            {/* Type and lead are both short pickers, so they share a row. */}
            <div className="ppc-tod-modal__row">
              <div className="ppc-tod-modal__field">
                <div className="ppc-tod-modal__label">Discipline type</div>
                <SelectPicker
                  cleanable={false}
                  searchable={false}
                  style={{ width: '100%' }}
                  data={TASK_TYPE_OPTIONS}
                  value={newTaskType}
                  onChange={(v) => setNewTaskType(v === 'odc' ? 'odc' : 'labor')}
                  placeholder="Select type"
                />
              </div>

              <div className="ppc-tod-modal__field">
                <div className="ppc-tod-modal__label">Discipline lead</div>
                <SelectPicker
                  cleanable
                  searchable
                  style={{ width: '100%' }}
                  data={projectMemberOptions}
                  value={Number.isInteger(newTaskLeadUserId) ? newTaskLeadUserId : null}
                  onChange={(v) => setNewTaskLeadUserId(Number.isInteger(v) ? v : null)}
                  placeholder={projectUsersLoading ? 'Loading members…' : 'Select lead'}
                  disabled={projectUsersLoading || projectMemberOptions.length === 0}
                />
              </div>
            </div>

            {createTask.isError ? (
              <Message type="error" showIcon>
                {String(createTask.error?.message || createTask.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              setAddTaskOpen(false);
              setNewTaskName('');
              setNewTaskBudgetTarget('');
              setNewTaskType('labor');
              setNewTaskLeadUserId(null);
            }}
            disabled={createTask.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton variant="primary" loading={createTask.isPending} disabled={createTask.isPending} onClick={submitAddTask}>
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={addTaskFromWbsOpen}
        onClose={() => {
          if (addTaskFromWbsPending || createTaskFromWbs.isPending) return;
          setAddTaskFromWbsOpen(false);
          setWbsTemplateId(null);
          setWbsTaskName('');
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>Add Discipline from WBS Template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {wbsTemplatesError ? (
            <Message type="error" showIcon>
              Failed to load WBS templates.
            </Message>
          ) : null}

          {Array.isArray(wbsTemplates) && wbsTemplates.length === 0 ? (
            <Message type="info" showIcon>
              No templates yet. Create one in the WBS Manager.
            </Message>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Discipline name</div>
              <Input value={wbsTaskName} onChange={(v) => setWbsTaskName(v)} placeholder="Enter discipline name" />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>WBS template</div>
              <SelectPicker
                cleanable={false}
                searchable
                style={{ width: '100%' }}
                placeholder={wbsTemplatesLoading ? 'Loading templates…' : 'Select a template'}
                data={(wbsTemplates || [])
                  .map((t) => ({ label: t?.name || '—', value: Number(t?.id) }))
                  .filter((o) => Number.isInteger(o.value))}
                value={Number.isInteger(Number(wbsTemplateId)) ? Number(wbsTemplateId) : null}
                onChange={(v) => {
                  const next = Number.isInteger(v) ? v : null;
                  setWbsTemplateId(next);
                  if (!String(wbsTaskName || '').trim()) {
                    const found = (wbsTemplates || []).find((t) => Number(t?.id) === Number(next));
                    const name = String(found?.name ?? '').trim();
                    if (name) setWbsTaskName(name);
                  }
                }}
                disabled={wbsTemplatesLoading || (wbsTemplates || []).length === 0 || addTaskFromWbsPending}
              />
            </div>

            {wbsItemsError ? (
              <Message type="error" showIcon>
                Failed to load template items.
              </Message>
            ) : null}

            {Number.isInteger(Number(wbsTemplateId)) ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Activities to add</div>
                <div
                  style={{
                    border: '1px solid #eee',
                    borderRadius: 10,
                    padding: 10,
                    maxHeight: 220,
                    overflowY: 'auto',
                  }}
                >
                  {wbsItemsLoading ? <div style={{ color: '#666' }}>Loading…</div> : null}
                  {!wbsItemsLoading && (wbsItems || []).length === 0 ? (
                    <div style={{ color: '#666', fontSize: 12 }}>No items in this template.</div>
                  ) : null}

                  {!wbsItemsLoading && sortedWbsItems.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <div style={{ fontSize: 12, color: '#666' }}>
                          {wbsSelectedItemIds.length} / {sortedWbsItems.length} selected
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Button
                            size="xs"
                            appearance="subtle"
                            onClick={() => setWbsSelectedItemIds(sortedWbsItems.map((i) => i.id))}
                            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                          >
                            Select all
                          </Button>
                          <Button
                            size="xs"
                            appearance="subtle"
                            onClick={() => setWbsSelectedItemIds([])}
                            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                          >
                            Clear
                          </Button>
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {sortedWbsItems.map((i, idx) => {
                          const checked = wbsSelectedItemIds.includes(i.id);
                          return (
                            <div
                              key={i.id}
                              style={{
                                padding: '10px 8px',
                                borderBottom: idx === sortedWbsItems.length - 1 ? 'none' : '1px solid #f0f0f0',
                              }}
                            >
                              <Checkbox
                                checked={checked}
                                onChange={(_value, nextChecked) => {
                                  setWbsSelectedItemIds((prev) => {
                                    const base = Array.isArray(prev) ? prev : [];
                                    if (nextChecked) return Array.from(new Set([...base, i.id]));
                                    return base.filter((x) => Number(x) !== i.id);
                                  });
                                }}
                                disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
                              >
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <div style={{ fontWeight: 700, lineHeight: '16px' }}>{i.title}</div>
                                  {i.assumptions ? (
                                    <div style={{ fontSize: 12, color: '#666', lineHeight: '16px' }}>{i.assumptions}</div>
                                  ) : null}
                                </div>
                              </Checkbox>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {createTaskFromWbs.isError ? (
              <Message type="error" showIcon>
                {String(createTaskFromWbs.error?.message || createTaskFromWbs.error)}
              </Message>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton
            onClick={() => {
              if (addTaskFromWbsPending || createTaskFromWbs.isPending) return;
              setAddTaskFromWbsOpen(false);
              setWbsTemplateId(null);
              setWbsTaskName('');
            }}
            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
          >
            Cancel
          </NsiButton>
          <NsiButton
            variant="primary"
            onClick={submitAddTaskFromWbs}
            loading={addTaskFromWbsPending || createTaskFromWbs.isPending}
            disabled={addTaskFromWbsPending || createTaskFromWbs.isPending}
          >
            Create
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
